import { hintsValuesType } from '@treezor/treezor-sca/dist/webauthn/types';

import { IDashboardConfigSettingsWallets } from '@/features/wallets/types/index';
import { TCountryCode } from '@/features/user/store/user.model';
import { TRestrictionsGroupType } from '@/store/models/restriction.model';
import { EScope } from '@/features/roles/store/models/role.model';
import { TFavoriteItemType } from '@/features/dashboard-users/store/support-user.model';

export interface IDashboardConfigSite {
  name: string;
  description?: string;
  keywords?: string;
  environment: string;
  logo?: {
    full?: string;
    minify?: string;
  };
  color?: string;
  path: string;
  shortName?: string;
  isLocal?: boolean;
}

export interface IDashboardConfigSupportZendesk {
  key: string;
  forms: number[];
}

export interface IDashboardConfigSupport {
  zendesk?: IDashboardConfigSupportZendesk;
}

export interface IDashboardConfigApiOauth {
  path: string;
  scopes: EScope[];
  availableScopes: string[];
  clientId: string;
  redirectUri: string;
}

export type TResponseType = 'code' | 'token';

export interface IDashboardConfigApiSso {
  path: string;
  clientId: string;
  scopes: EScope[];
  responseType: TResponseType;
  redirectUri: string;
}

export type TDashboardConfigApiEncryption = 'hexa' | 'default';

export interface IDashboardConfigWebhooks {
  path: string;
}
export interface IDashboardConfigApi {
  baseUrl: string;
  webhooks: IDashboardConfigWebhooks;
  encryption?: TDashboardConfigApiEncryption;
  oauth: IDashboardConfigApiOauth;
  sso: IDashboardConfigApiSso;
  redirectUri?: string;
}

export interface IDashboardConfigSettingsCardsDefaultDeliveryAddress {
  address1: string;
  address2?: string;
  address3?: string;
  postCode: string;
  city: string;
  state?: string;
  country: string;
}

export type TDashboardConfigSettingsCardsDesignType = 'virtual' | 'physic';

export type TDashboardConfigSettingsCardsDesignCode = {
  label: string;
  value: string;
};

export interface IDashboardConfigSettingsCardsDesign {
  label: string;
  value: number;
  type?: TDashboardConfigSettingsCardsDesignType;
  limits?: IDashboardConfigSettingsCardsLimits;
  category?: string;
  designCodes?: TDashboardConfigSettingsCardsDesignCode[];
}

export interface IDashboardConfigSettingsCardsLimitsAtm {
  year: number;
  month: number;
  week: number;
  day: number;
  all: number;
}

export interface IDashboardConfigSettingsCardsLimitsPayment {
  year: number;
  month: number;
  week: number;
  day: number;
  all: number;
}

export interface IDashboardConfigSettingsCardsLimits {
  atm: IDashboardConfigSettingsCardsLimitsAtm;
  payment: IDashboardConfigSettingsCardsLimitsPayment;
}

export type TDashboardConfigSettingsCardsSettings = 'options' | 'block' | 'pin' | 'limits' | 'restrictions';

export interface IDashboardConfigSettingsCards {
  defaultDeliveryAddress?: IDashboardConfigSettingsCardsDefaultDeliveryAddress;
  designs: IDashboardConfigSettingsCardsDesign[];
  limits?: IDashboardConfigSettingsCardsLimits;
  settings?: TDashboardConfigSettingsCardsSettings[];
  restrictions?: TRestrictionsGroupType[];
  digitizedCards?: Record<PropertyKey, never>;
}

export interface IDashboardConfigSettingsPayouts {
  controls?: {
    amountPhysicalUser?: number;
    amountBusinessUser?: number;
  };
  instantTransfer?: number;
  tags?: string[];
}

export type TDashboardConfigSettingsBulkCardsPermOptions = 'nfc' | 'atm' | 'online' | 'foreign' | 'pinUnverified';

export interface IDashboardConfigSettingsBulkCards {
  permsGroup?: TDashboardConfigSettingsBulkCardsPermOptions[];
  batchDelivery?: IDashboardConfigBatchDelivery;
}

export type TDashboardConfigSettings2FAOptions = 'allSupported' | 'platform' | 'crossPlatform';

export interface IDashboardConfigSettings2FA {
  attachment: TDashboardConfigSettings2FAOptions;
  hints?: hintsValuesType[];
}

export interface IDashboardConfigSettingsPCIDSS {}

export interface IDashboardConfigBatchDelivery {}

export type TDashboardConfigSettingsUsersTab =
  | 'overview'
  | 'kyckyb'
  | 'wallets'
  | 'cards'
  | 'beneficiaries'
  | 'children'
  | 'companyMembers';

export type TDashboardConfigSettingsScreen =
  | 'search'
  | 'profiles'
  | 'allUsers'
  | 'createUser'
  | 'allCardTransactions'
  | 'allCards'
  | 'kycNeedReview'
  | 'bulkCards'
  | 'onboarding'
  | 'supportUsers'
  | 'restrictions'
  | 'webhooks'
  | 'templates'
  | 'configurations'
  | 'encryption'
  | 'allWallets'
  | 'allBeneficiaries';

export const DashboardSearchGroupKeys = ['user', 'wallet', 'card', 'beneficiary'] as const;

export type TDashboardConfigSettingsSearchGroup = (typeof DashboardSearchGroupKeys)[number];

export type TDashboardConfigSettingsSearch =
  | 'userId'
  | 'userTag'
  | 'email'
  | 'userName'
  | 'walletId'
  | 'walletIban'
  | 'cardToken'
  | 'cardEmbossedName'
  | 'beneficiaryId'
  | 'beneficiaryIban'
  | 'beneficiaryName';

export type TDashboardConfigUserActions =
  | 'deleteUser'
  | 'sendForgotPassword'
  | 'addKycDocument'
  | 'reviewKyc'
  | 'downloadOperations'
  | 'transfer'
  | 'payout'
  | 'addWallet'
  | 'addBeneficiary'
  | 'addChild'
  | 'createCard'
  | 'freezeUserTag'
  | 'freezeCardTag'
  | 'hideUnblockCVC'
  | 'hideCloseCard'
  | 'restrictUserInfo';

export type TUserCreationType =
  | 'physical'
  | 'business'
  | 'selfEmployed'
  | 'parent'
  | 'nonGovernmental'
  | 'governmental';

export type TDataQuality = 'personalSituation';

export type TDashboardConfigSettingsBeneficiaries = 'sddr' | 'scte';

export type TDashboardBeneficiariyModal = 'scte' | 'sddrB2C' | 'sddrB2B';

export type TDashboardConfigSettingsUsersFavorites = Record<TFavoriteItemType, number>;

export const kycStatusConfig = {
  notRequired: 'notRequired',
  required: 'required',
  optional: 'optional',
} as const;

export type TKycStatusConfig = keyof typeof kycStatusConfig;

export const configUserRole = {
  selfEmployed: 'selfEmployed',
  employee: 'employee',
  child: 'child',
  otherPhysical: 'otherPhysical',
} as const;

export type TConfigUserRole = keyof typeof configUserRole;

type TRoleKycStatus = Record<TConfigUserRole, TKycStatusConfig>;

export interface IDashboardConfigSettingsUsersKyc {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  useAdditionalVigilanceMeasure: boolean;
  documentTypeId?: number[];
  status: TRoleKycStatus;
}

export interface IDashboardConfigSettingsUsers {
  tabs: TDashboardConfigSettingsUsersTab[];
  actions?: TDashboardConfigUserActions[];
  kyc?: IDashboardConfigSettingsUsersKyc;
  distributionCountry?: TCountryCode[];
  creation?: TUserCreationType[];
  favorites?: TDashboardConfigSettingsUsersFavorites;
  dataQuality?: TDataQuality[];
}

export type TDashboardConfigSettingsConfigurationTabs = 'settings' | 'security' | 'integration';

export interface IDashboardConfigSettingsConfiguration {
  tabs?: TDashboardConfigSettingsConfigurationTabs[];
}

export interface IDashboardConfigSettingsSca {}

export interface IDashboardConfigSettingsDashboardUsers {
  accessExpiration: number;
}

export interface IDashboardConfigSettings {
  users?: IDashboardConfigSettingsUsers;
  cards: IDashboardConfigSettingsCards;
  wallets: IDashboardConfigSettingsWallets;
  searchBar?: TDashboardConfigSettingsSearch[];
  screens?: TDashboardConfigSettingsScreen[];
  beneficiaries?: TDashboardConfigSettingsBeneficiaries[];
  configuration?: IDashboardConfigSettingsConfiguration;
  sca?: IDashboardConfigSettingsConfiguration;
  payouts?: IDashboardConfigSettingsPayouts;
  bulkCards?: IDashboardConfigSettingsBulkCards;
  monitoring?: {
    matomo: string;
    rum: {
      endpoint: string;
      alias: string;
      appId: string;
      appVersion: string;
      appRegion: string;
    };
  };
  '2fa'?: IDashboardConfigSettings2FA;
  dashboardUsers?: IDashboardConfigSettingsDashboardUsers;
  pcidss?: IDashboardConfigSettingsPCIDSS;
}

export interface IDashboardConfig {
  site: IDashboardConfigSite;
  support?: IDashboardConfigSupport;
  api: IDashboardConfigApi;
  settings: IDashboardConfigSettings;
}
